import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Link2,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  XeroStatusError,
  XeroStatusLoading,
} from "@/components/admin/XeroStatusStates";
import { REQUIRED_SCOPES, describeScope } from "@/lib/xeroScopes";
import {
  XERO_STATUS_QUERY_KEY,
  getXeroStatus,
  startXeroConnect,
} from "@/services/xeroApi";

function ScopeList({ scope }: { scope: string | null }) {
  const granted = (scope ?? "").split(" ").filter(Boolean);
  if (granted.length === 0) {
    return <p className="text-sm text-muted-foreground">No scope recorded.</p>;
  }

  // Xero returns the scope in whatever order it likes. Sorting the ones Prism
  // depends on to the top puts the two that can break the pipeline where an
  // admin looks first.
  const sorted = [...granted].sort((a, b) => {
    const byRequired =
      Number(!!describeScope(b).required) - Number(!!describeScope(a).required);
    return byRequired !== 0 ? byRequired : a.localeCompare(b);
  });

  const missing = REQUIRED_SCOPES.filter((item) => !granted.includes(item));

  return (
    <div className="space-y-3">
      {missing.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-600/5 p-3 text-sm text-amber-700 dark:text-amber-500">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The consent is missing {missing.join(" and ")}, which Prism needs.
            Reconnect and make sure {missing.length === 1 ? "it is" : "they are"}{" "}
            included.
          </span>
        </div>
      )}

      <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
        {sorted.map((item) => {
          const { description, required } = describeScope(item);
          return (
            <li
              key={item}
              className="flex flex-col gap-1.5 p-3 sm:flex-row sm:items-baseline sm:gap-4"
            >
              <div className="flex shrink-0 items-center gap-2 sm:w-[240px]">
                <code className="font-mono text-[11px] text-foreground">
                  {item}
                </code>
                {required && (
                  <Badge
                    variant="secondary"
                    className="shrink-0 text-[10px] font-normal"
                  >
                    Required
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{description}</p>
            </li>
          );
        })}
      </ul>

      <p className="text-xs text-muted-foreground">
        The scope is set in Key Vault under xero-oauth-scope, so changing it does
        not need a redeploy. Reconnecting is what applies a change.
      </p>
    </div>
  );
}

export default function XeroConnectionTab() {
  const { toast } = useToast();
  const [connecting, setConnecting] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: XERO_STATUS_QUERY_KEY,
    queryFn: getXeroStatus,
  });

  const connect = async () => {
    setConnecting(true);
    try {
      await startXeroConnect();
    } catch (e) {
      setConnecting(false);
      toast({
        variant: "destructive",
        title: "Couldn’t start the Xero connection",
        description: (e as Error).message,
      });
    }
  };

  if (isLoading) {
    return <XeroStatusLoading />;
  }

  if (isError) {
    return (
      <XeroStatusError
        message={(error as Error)?.message}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data?.connected) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center">
        <Link2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-medium text-foreground">
          Xero isn’t connected yet
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Connect a Xero account once to let Prism read your organisations’
          data. You’ll be taken to Xero to sign in and choose which
          organisations to authorise. This is a one-time setup. Prism keeps the
          connection alive afterwards.
        </p>
        <Button onClick={connect} disabled={connecting} className="mt-5 gap-2">
          {connecting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}
          Set up Xero connection
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-medium text-foreground">
            Connected to Xero
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={connect}
            disabled={connecting}
            className="gap-2"
          >
            {connecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            Add Entity
          </Button>
        </div>
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">Granted scope</h3>
        <p className="text-xs text-muted-foreground">
          What this connection permits Prism to do in Xero. Anything not listed
          here is refused by Xero, whether or not Prism asks for it.
        </p>
        <ScopeList scope={data.scope} />
      </section>

      {data.connected_at && (
        <p className="text-xs text-muted-foreground">
          Connected {new Date(data.connected_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
